import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { inr } from "@/lib/utils";
import { TrendingUp, TrendingDown, PiggyBank, Loader2, BarChart3 } from "lucide-react";

/**
 * Iter51 — Monthly Financial Dashboard (admin-only)
 *
 * Provides a month-scoped view of studio cash-flow:
 *   • Inflow  = Σ received payment milestones
 *   • Outflow = payroll payments + office expenses (debits)
 *   • Net     = Inflow − Outflow
 *   • Weekly bar chart comparing week-by-week inflow vs outflow.
 *
 * Backed by `GET /api/dashboard/monthly-financials?month=YYYY-MM`. The
 * endpoint is gated by `require_admin`; this component is only mounted for
 * admins on Dashboard.jsx so no client-side role guard is needed here.
 */

// --- helpers -----------------------------------------------------------------
function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Generate 24 months (12 back, 11 forward) around today for the dropdown.
function buildMonthOptions() {
  const out = [];
  const now = new Date();
  for (let i = -12; i <= 11; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    out.push({ value, label });
  }
  return out;
}

// Formats a Sun-start week as "5 Jul → 11 Jul".
function fmtWeek(start, end) {
  try {
    const [ys, ms, ds] = start.split("-").map(Number);
    const [ye, me, de] = end.split("-").map(Number);
    const s = new Date(Date.UTC(ys, ms - 1, ds));
    const e = new Date(Date.UTC(ye, me - 1, de));
    const opt = { day: "numeric", month: "short", timeZone: "UTC" };
    return `${s.toLocaleDateString("en-IN", opt)} → ${e.toLocaleDateString("en-IN", opt)}`;
  } catch {
    return `${start} → ${end}`;
  }
}

export default function MonthlyFinancials() {
  const [month, setMonth] = useState(currentYm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const months = useMemo(buildMonthOptions, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/monthly-financials?month=${month}`);
      setData(data);
    } catch (e) {
      logger.debug("monthly financials load failed:", e?.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const net = data?.net ?? 0;
  const netPositive = net >= 0;
  const maxBar = useMemo(() => {
    if (!data?.weekly) return 1;
    // Both bars share the same axis so max of both values across all weeks.
    let m = 0;
    for (const w of data.weekly) {
      m = Math.max(m, w.inflow || 0, w.outflow || 0);
    }
    return m > 0 ? m : 1;
  }, [data]);

  return (
    <section className="space-y-4" data-testid="monthly-financials-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Financial dashboard · admin only</div>
          <h2 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-rose-700" strokeWidth={1.8} />
            Monthly Financials
          </h2>
        </div>
        <label className="flex items-center gap-2 text-xs text-stone-500">
          <span className="uppercase tracking-widest font-semibold">Month</span>
          <select
            className="input py-1.5 text-sm font-semibold min-w-[10rem]"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="monthly-financials-month-select"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Three master cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FinancialCard
          testid="mf-card-inflow"
          icon={TrendingUp}
          label="Total Inflow"
          sub="Client payments received"
          value={inr(data?.inflow?.total || 0)}
          meta={`${data?.inflow?.count || 0} milestone${(data?.inflow?.count || 0) === 1 ? "" : "s"}`}
          tone="emerald"
          loading={loading}
        />
        <FinancialCard
          testid="mf-card-outflow"
          icon={TrendingDown}
          label="Total Outflow"
          sub="Team payouts + office expenses"
          value={inr(data?.outflow?.total || 0)}
          meta={
            data
              ? `Payroll ${inr(data.outflow?.payroll?.total || 0)} · Office ${inr(data.outflow?.office?.total || 0)}`
              : ""
          }
          tone="rose"
          loading={loading}
        />
        <FinancialCard
          testid="mf-card-net"
          icon={PiggyBank}
          label="Net Monthly Profit"
          sub={netPositive ? "You're in the green ✓" : "Spending exceeded receipts"}
          value={`${netPositive ? "" : "-"}${inr(Math.abs(net))}`}
          meta={data ? `${data.month_label}` : ""}
          tone={netPositive ? "emerald" : "red"}
          loading={loading}
        />
      </div>

      {/* Weekly bar chart */}
      <div className="card p-4" data-testid="monthly-financials-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-stone-800">Week-by-week · Inflow vs Outflow</div>
          <div className="flex items-center gap-3 text-xs text-stone-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Inflow</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />Outflow</span>
          </div>
        </div>
        {loading ? (
          <div className="py-10 text-center text-stone-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (data?.weekly || []).length === 0 ? (
          <div className="py-10 text-center text-stone-400 text-sm">No weeks to show for this month.</div>
        ) : (
          <div className="space-y-3">
            {data.weekly.map((w) => (
              <WeekRow key={w.week_start} w={w} max={maxBar} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FinancialCard({ icon: Icon, label, sub, value, meta, tone, loading, testid }) {
  // Tone maps — keep Tailwind classnames literal so purge doesn't drop them.
  const toneClass = {
    emerald: { bg: "bg-emerald-50", ring: "border-emerald-200", txt: "text-emerald-800", chip: "text-emerald-700" },
    rose:    { bg: "bg-rose-50",    ring: "border-rose-200",    txt: "text-rose-800",    chip: "text-rose-700"    },
    red:     { bg: "bg-red-50",     ring: "border-red-200",     txt: "text-red-800",     chip: "text-red-700"     },
  }[tone] || { bg: "bg-stone-50", ring: "border-stone-200", txt: "text-stone-800", chip: "text-stone-600" };

  return (
    <div className={`card p-5 border-2 ${toneClass.ring}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
          <div className={`font-heading text-3xl font-semibold mt-2 tabular-nums ${toneClass.txt}`}>
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-stone-300" /> : value}
          </div>
          <div className="text-xs text-stone-500 mt-1.5">{sub}</div>
          {meta && <div className={`text-[10px] mt-1.5 font-semibold uppercase tracking-wider ${toneClass.chip}`}>{meta}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClass.bg} ${toneClass.txt}`}>
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
      </div>
    </div>
  );
}

function WeekRow({ w, max }) {
  // Two side-by-side horizontal bars (inflow above, outflow below) sharing
  // the same scale. This keeps the visual comparison honest and fits any
  // container width without a chart lib.
  const inflowPct = Math.max(2, Math.round((w.inflow / max) * 100));
  const outflowPct = Math.max(2, Math.round((w.outflow / max) * 100));
  const empty = !w.inflow && !w.outflow;
  return (
    <div className={empty ? "opacity-40" : ""} data-testid={`mf-week-${w.week_start}`}>
      <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
        <span className="font-mono font-semibold text-stone-700">{fmtWeek(w.week_start, w.week_end)}</span>
        <span className="tabular-nums text-stone-500">
          <span className="text-emerald-700 font-semibold">{inr(w.inflow || 0)}</span>
          <span className="mx-1.5">·</span>
          <span className="text-rose-700 font-semibold">{inr(w.outflow || 0)}</span>
        </span>
      </div>
      <div className="space-y-1">
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${w.inflow > 0 ? inflowPct : 0}%` }} data-testid={`mf-bar-inflow-${w.week_start}`} />
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-rose-500 transition-all" style={{ width: `${w.outflow > 0 ? outflowPct : 0}%` }} data-testid={`mf-bar-outflow-${w.week_start}`} />
        </div>
      </div>
    </div>
  );
}
